import { resolveSegmentDecision } from '../../../../shared/vacations/segmentDecisions';
import { format } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import { getEffectiveSegmentDates } from 'shared/vacations/effectiveSegments';
import { isApprovedVacationRequest, resolveManagerEmail } from './vacationRequestStatus';
import { TIMELINE_LABELS } from '../vacations.definitions';
import type { HistoryRequestMilestone, HistoryEventKind } from '../models/vacationHistoryReport.types';
import type { VacationRequest } from '../models/vacation.types';

export function buildRequestMilestones(
    request: VacationRequest,
    collaboratorMaps: { byEmail?: Map<string, { name: string; id: string }> },
    today: Date = new Date()
): HistoryRequestMilestone[] {
    const milestones: HistoryRequestMilestone[] = [];

    milestones.push({
        kind: 'requested',
        occurredAt: request.requestedAt?.toDate?.() ?? null,
        actorEmail: request.requestedBy ?? null,
        actorName: resolveName(request.requestedBy, collaboratorMaps),
    });

    if (request.managerApproval.status === 'Approved') {
        milestones.push({
            kind: 'managerApproved',
            occurredAt: request.managerApproval.at?.toDate?.() ?? null,
            actorEmail: request.managerApproval.by ?? null,
            actorName: resolveName(request.managerApproval.by, collaboratorMaps),
        });
    } else if (request.managerApproval.status === 'Rejected') {
        milestones.push({
            kind: 'rejected',
            occurredAt: request.managerApproval.at?.toDate?.() ?? null,
            actorEmail: request.managerApproval.by ?? null,
            actorName: resolveName(request.managerApproval.by, collaboratorMaps),
        });
        return milestones;
    }

    if (request.clientApproval.status === 'Approved') {
        milestones.push({
            kind: 'clientApproved',
            occurredAt: request.clientApproval.at?.toDate?.() ?? null,
            actorEmail: request.clientApproval.by ?? null,
            actorName: resolveName(request.clientApproval.by, collaboratorMaps),
        });
    }

    if (request.dpApproval.status === 'Approved') {
        milestones.push({
            kind: 'dpApproved',
            occurredAt: request.dpApproval.at?.toDate?.() ?? null,
            actorEmail: request.dpApproval.by ?? null,
            actorName: resolveName(request.dpApproval.by, collaboratorMaps),
        });
    } else if (request.dpApproval.status === 'Rejected') {
        milestones.push({
            kind: 'rejected',
            occurredAt: request.dpApproval.at?.toDate?.() ?? null,
            actorEmail: request.dpApproval.by ?? null,
            actorName: resolveName(request.dpApproval.by, collaboratorMaps),
        });
        return milestones;
    }

    if (isApprovedVacationRequest(request)) {
        const sortedSegments = [...request.segments]
            .filter((s) => s.startDate && s.endDate)
            .sort((a, b) => a.startDate.localeCompare(b.startDate));

        sortedSegments.forEach((segment) => {
            const decision = resolveSegmentDecision(request, segment.order);

            if (decision?.status === 'Rejected') {
                return;
            }

            const effectiveDates = getEffectiveSegmentDates(segment, decision);
            const today_iso = format(today, 'yyyy-MM-dd');

            const startDateObj = createDateSafe(effectiveDates.startDate);
            const endDateObj = createDateSafe(effectiveDates.endDate);
            const isStartProjected = effectiveDates.startDate > today_iso;
            const isEndProjected = effectiveDates.endDate > today_iso;

            milestones.push({
                kind: 'started',
                occurredAt: startDateObj,
                actorEmail: null,
                segmentOrder: segment.order,
                isProjected: isStartProjected,
            });

            milestones.push({
                kind: 'completed',
                occurredAt: endDateObj,
                actorEmail: null,
                segmentOrder: segment.order,
                isProjected: isEndProjected,
            });
        });
    } else if (request.status === 'Rejected' || request.expiredAtStage) {
        milestones.push({
            kind: 'rejected',
            occurredAt: request.statusChangedAt?.toDate?.() ?? null,
            actorEmail: request.statusChangedBy === 'system' ? null : null,
        });
    }

    return milestones;
}

function resolveName(
    email: string | null | undefined,
    maps: { byEmail?: Map<string, { name: string; id: string }> }
): string | undefined {
    if (!email || !maps.byEmail) return undefined;
    return maps.byEmail.get(email.toLowerCase())?.name;
}

export function getMilestoneLabel(kind: HistoryEventKind, segmentOrder?: number): string {
    if (kind === 'requested') return TIMELINE_LABELS.requested;
    if (kind === 'managerApproved') return TIMELINE_LABELS.managerApproved;
    if (kind === 'clientApproved') return TIMELINE_LABELS.clientApproved;
    if (kind === 'dpApproved') return TIMELINE_LABELS.dpApproved;
    if (kind === 'rejected') return TIMELINE_LABELS.dpRejected;
    if (kind === 'started') {
        if (segmentOrder !== undefined) {
            return `${['1ª', '2ª', '3ª'][segmentOrder - 1] ?? `${segmentOrder}ª`} parcela iniciada`;
        }
        return TIMELINE_LABELS.started;
    }
    if (kind === 'completed') {
        if (segmentOrder !== undefined) {
            return `${['1ª', '2ª', '3ª'][segmentOrder - 1] ?? `${segmentOrder}ª`} parcela concluída`;
        }
        return TIMELINE_LABELS.completed;
    }
    return '';
}
